import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { useOpsReadModel } from './use-ops-read-model.js';
import type { Panel } from './use-ops-read-model.js';

function Stat({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <div style={{ padding: 10, borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
      <div style={{ color: C.textMuted, fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: accent || C.text, fontSize: 18, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function PanelShell<T>({ title, panel, children }: { title: string; panel: Panel<T>; children: (data: T) => React.ReactNode }) {
  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold }}>
          {title}
        </div>
        {panel.loading && <div style={{ color: C.textMuted, fontSize: 11 }}>Loading…</div>}
      </div>
      {panel.error && (
        <div style={{ color: C.textMuted, fontSize: 12 }}>
          Unavailable: {panel.error}
        </div>
      )}
      {!panel.loading && !panel.error && panel.data && children(panel.data)}
    </div>
  );
}

export default function OpsModule() {
  const { fleet, safety, finance, refreshOps } = useOpsReadModel();

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted }}>
          Ops module
        </div>
        <button
          type="button"
          onClick={refreshOps}
          style={{
            background: 'none',
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 6,
            color: C.textMuted,
            padding: '4px 10px',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Refresh
        </button>
      </div>
      <p style={{ marginTop: 8, marginBottom: 4, lineHeight: 1.6 }}>
        Ops is a synthesized operational readout, not a source-of-truth backbone.
        Every number below is pulled live from the owning domain's real backend
        path — Fleet, Safety, and Finance — and aggregated here for at-a-glance
        pressure. Each panel loads independently, so a permission gate on one
        domain cannot sink the others.
      </p>

      <PanelShell title="Fleet pressure" panel={fleet}>
        {(data) => (
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
            <Stat label="Total vehicles" value={data.total} />
            <Stat label="Active"         value={data.active}         accent={C.green} />
            <Stat label="Out of service" value={data.oos}            accent={data.oos > 0 ? C.red : C.textMuted} />
            <Stat label="In transit"     value={data.in_transit}     accent={data.in_transit > 0 ? C.gold : C.textMuted} />
            <Stat label="Open issues"    value={data.openIssues}     accent={data.openIssues > 0 ? C.red : C.textMuted} />
            <Stat label="Vehicles w/ issues" value={data.vehiclesWithIssues} accent={data.vehiclesWithIssues > 0 ? C.red : C.textMuted} />
          </div>
        )}
      </PanelShell>

      <PanelShell title="Safety compliance" panel={safety}>
        {(data) => {
          const truncated = data.total > data.loaded;
          return (
            <>
              <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
                <Stat
                  label={truncated ? `Loaded (of ${data.total})` : 'Meetings loaded'}
                  value={data.loaded}
                />
                <Stat label="Open"              value={data.open}              accent={data.open > 0 ? C.gold : C.textMuted} />
                <Stat label="Closed"            value={data.closed}            accent={C.green} />
                <Stat label="Terminals covered" value={data.terminalsCovered} />
                <Stat
                  label="Days since latest"
                  value={data.daysSinceLatest == null ? '—' : data.daysSinceLatest}
                  accent={data.daysSinceLatest == null ? C.textMuted : data.daysSinceLatest > 30 ? C.red : data.daysSinceLatest > 7 ? C.gold : C.green}
                />
              </div>
              {truncated && (
                <div style={{ color: C.textMuted, fontSize: 11, marginTop: 8 }}>
                  Safety panel reflects the most recent 50 meetings; open `Safety` tab for the full list.
                </div>
              )}
            </>
          );
        }}
      </PanelShell>

      <PanelShell title="Finance health — last week" panel={finance}>
        {(data) => (
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
            <Stat label="Settlements" value={data.total} />
            <Stat label="Verified"    value={data.verified} accent={C.green} />
            <Stat label="Pending"     value={data.pending}  accent={data.pending > 0 ? C.gold : C.textMuted} />
            <Stat label="Missing"     value={data.missing}  accent={data.missing > 0 ? C.red : C.textMuted} />
            <Stat label="Variance"    value={data.variance} accent={data.variance > 0 ? C.red : C.textMuted} />
          </div>
        )}
      </PanelShell>
    </div>
  );
}
